/**
 * Case access for the canvas surfaces (ACC03).
 *
 * A viewer collaborator reads a case and never acts on it: the server answers 403 to every route that runs an
 * operator, starts a run, screens, builds or generates on the case. The controls read `can_edit` from the case
 * payload (`GET /cases/{id}`, same value as the share dialog) and show a disabled control with the reason, so
 * nothing that would 403 is shown enabled.
 *
 * The flag lives in a small store so the deep canvas components (palette, radial menu, intake cards) and the
 * non-React run helpers read one answer. Unknown (not loaded yet, or the read failed) blocks: fail closed.
 * A surface with no case id (demo, preview) is not blocked: nothing is written to a case there.
 */
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { caseService } from '../services/api';

export const VIEWER_ACTION_REASON =
  'You can read this case but not change it. Only the case owner or an editor can run operators, start runs, screen, build or generate here.';

interface CaseAccessState {
  caseId: string | undefined;
  /** true / false once the case read answered; null while it is loading or when it failed. */
  canEdit: boolean | null;
  begin: (caseId: string | undefined) => void;
  settle: (caseId: string, canEdit: boolean | null) => void;
}

export const useCaseAccessStore = create<CaseAccessState>((set, get) => ({
  caseId: undefined,
  canEdit: null,
  begin: (caseId) => set({ caseId, canEdit: null }),
  // R8: an answer for a case that is no longer the current one never lands.
  settle: (caseId, canEdit) => { if (get().caseId === caseId) set({ canEdit }); },
}));

/** True when a control that writes to the case must be shown disabled. */
export function isCaseWriteBlocked(s: { caseId: string | undefined; canEdit: boolean | null }): boolean {
  return !!s.caseId && s.canEdit !== true;
}

/** Non-React read, for the run helpers (a second line behind the disabled controls). */
export function caseWriteBlockedNow(): boolean {
  return isCaseWriteBlocked(useCaseAccessStore.getState());
}

/** For a component that owns the case id: loads the flag for it (R8 `cancelled` flag) and returns the block state. */
export function useLoadCaseAccess(caseId: string | undefined): void {
  useEffect(() => {
    const store = useCaseAccessStore.getState();
    store.begin(caseId);
    if (!caseId) return;
    let cancelled = false;
    caseService.get(caseId)
      .then((c: { can_edit?: boolean | null }) => { if (!cancelled) useCaseAccessStore.getState().settle(caseId, c?.can_edit === true); })
      .catch(() => { if (!cancelled) useCaseAccessStore.getState().settle(caseId, false); });
    return () => {
      cancelled = true;
      // Leaving the case: a later surface with no case id (demo, /fusion) is not blocked by this case's answer.
      if (useCaseAccessStore.getState().caseId === caseId) useCaseAccessStore.getState().begin(undefined);
    };
  }, [caseId]);
}

export const CHECKING_ACCESS_REASON = 'Checking your access to this case.';

/** For any control: `{ blocked, reason }`. `reason` is the empty string when the control may be live. */
export function useCaseWriteBlock(): { blocked: boolean; reason: string } {
  const blocked = useCaseAccessStore((s) => isCaseWriteBlocked(s));
  const known = useCaseAccessStore((s) => s.canEdit !== null);
  return { blocked, reason: !blocked ? '' : known ? VIEWER_ACTION_REASON : CHECKING_ACCESS_REASON };
}

/**
 * Self-contained twin for a surface that owns a case id and is not the canvas (the report page, the notice
 * dialogs): reads `can_edit` itself (R8 `cancelled` flag) and does not touch the canvas store. No case id means
 * no case to protect (a notice with no case is only rendered, never stored), so it is not blocked.
 */
export function useCaseEditBlock(caseId: string | undefined): { blocked: boolean; reason: string } {
  const [state, setState] = useState<{ caseId: string | undefined; canEdit: boolean | null }>({ caseId, canEdit: null });
  useEffect(() => {
    setState({ caseId, canEdit: null });
    if (!caseId) return;
    let cancelled = false;
    caseService.get(caseId)
      .then((c: { can_edit?: boolean | null }) => { if (!cancelled) setState({ caseId, canEdit: c?.can_edit === true }); })
      .catch(() => { if (!cancelled) setState({ caseId, canEdit: false }); });
    return () => { cancelled = true; };
  }, [caseId]);
  const current = state.caseId === caseId ? state.canEdit : null;
  const blocked = !!caseId && current !== true;
  return { blocked, reason: !blocked ? '' : current === null ? CHECKING_ACCESS_REASON : VIEWER_ACTION_REASON };
}
